import { useEffect, useMemo, useState } from "react";
import LoadingWheel from "../shared/LoadingWheel";
import useVehiclePricingMatrix from "../../data/useVehiclePricingMatrix";
import PriceFormat from "../../utils/priceFormat";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import classNames from "../../utils/classNames";

const accountTypeLabelMap: Record<number, string> = {
  5: "Reuse",
  6: "Remanufacturing",
  7: "Reintegration",
  8: "Recycling",
  9: "Scrap",
};

const accountTypeLabelClassMap: Record<number, string> = {
  5: "text-emerald-700",
  6: "text-orange-700",
  7: "text-blue-700",
  8: "text-purple-700",
  9: "text-red-700",
};

interface VehiclePricingMatrixProps {
  vehicleId?: string;
}

export default function VehiclePricingMatrix({ vehicleId }: VehiclePricingMatrixProps) {
  const { vehiclePricingMatrix, updateVehiclePricingMatrix } = useVehiclePricingMatrix(vehicleId, "VehiclePricingMatrix");
  const { saveHandlers } = useDefaultCRUDHandlers("Vehicle Pricing Matrix");
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>({});
  const accounts = vehiclePricingMatrix.data?.data?.accounts ?? [];
  const categories = vehiclePricingMatrix.data?.data?.categories ?? [];
  const cellDataByKey = useMemo(() => {
    const data: Record<string, { enabled: boolean; price: number | null }> = {};

    categories.forEach((category) => {
      category.cells.forEach((cell) => {
        const key = `${category.partCategoryId}-${cell.accountId}`;
        data[key] = {
          enabled: cell.enabled,
          price: cell.price,
        };
      });
    });

    return data;
  }, [categories]);

  useEffect(() => {
    const nextInputs: Record<string, string> = {};

    categories.forEach((category) => {
      category.cells.forEach((cell) => {
        const key = `${category.partCategoryId}-${cell.accountId}`;
        nextInputs[key] = cell.price != null ? String(cell.price) : "";
      });
    });

    setPriceInputs(nextInputs);
  }, [categories]);

  if (vehiclePricingMatrix.isLoading) {
    return (
      <div className="p-4 bg-white rounded shadow min-h-[220px] flex items-center justify-center">
        <LoadingWheel />
      </div>
    );
  }

  if (vehiclePricingMatrix.isError) {
    return (
      <div className="p-4 bg-white rounded shadow text-red-600 font-medium">
        Unable to load pricing matrix.
      </div>
    );
  }

  if (!accounts.length) {
    return (
      <div className="p-4 bg-white rounded shadow text-sm text-gray-600">
        No reuse accounts are configured.
      </div>
    );
  }

  if (!categories.length) {
    return (
      <div className="p-4 bg-white rounded shadow text-sm text-gray-600">
        No vehicle part categories found for pricing.
      </div>
    );
  }

  const handleCellBlur = (partCategoryId: string, accountId: string) => {
    const key = `${partCategoryId}-${accountId}`;
    const cellData = cellDataByKey[key];
    if (!cellData?.enabled) return;

    const value = priceInputs[key]?.trim() ?? "";
    const parsed = value === "" ? null : Number(value);

    if (value !== "" && Number.isNaN(parsed)) {
      setPriceInputs((prev) => ({
        ...prev,
        [key]: cellData.price != null ? String(cellData.price) : "",
      }));
      return;
    }

    if (parsed === cellData.price) return;

    updateVehiclePricingMatrix.mutate(
      {
        prices: [{ accountId, partCategoryId, price: parsed }],
      },
      saveHandlers
    );
  };

  const getBestPrice = (partCategoryId: string) => {
    const values = accounts
      .map((account) => {
        const key = `${partCategoryId}-${account.accountId}`;
        const cellData = cellDataByKey[key];
        if (!cellData?.enabled) return null;

        const value = priceInputs[key]?.trim() ?? "";
        if (!value) return null;

        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
      })
      .filter((value): value is number => value != null);

    if (values.length === 0) return null;
    return Math.max(...values);
  };

  const totalBest = categories.reduce((sum, category) => {
    const bestPrice = getBestPrice(category.partCategoryId);
    return sum + (bestPrice ?? 0);
  }, 0);

  return (
    <div className="bg-white rounded shadow p-4">
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-200 rounded-md">
          <thead className="bg-gray-100 [&_th]:border-gray-200 [&_th]:border-b [&_th]:border-r">
            <tr>
              <th className="text-left px-3 py-2 text-sm font-semibold text-gray-800">
                Part Category
              </th>
              {accounts.map((account) => (
                <th
                  key={account.accountId}
                  className="text-center px-3 py-2 text-sm font-semibold text-gray-800"
                >
                  <div className="flex flex-col">
                    <span>{account.accountName}</span>
                    <span
                      className={classNames(
                        "text-xs font-normal",
                        accountTypeLabelClassMap[account.accountType] ?? "text-gray-500"
                      )}
                    >
                      {accountTypeLabelMap[account.accountType] ?? `Type ${account.accountType}`}
                    </span>
                  </div>
                </th>
              ))}
              <th className="text-center px-3 py-2 text-sm font-semibold text-gray-800 min-w-[60px]">
                Best
              </th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category) => {
              const bestPrice = getBestPrice(category.partCategoryId);

              return (
                <tr key={category.partCategoryId} className="border-b [&_td]:border-r border-gray-200">
                  <td className="px-3 py-2 text-sm text-gray-800 font-medium">
                    {category.partCategoryName}
                  </td>
                  {accounts.map((account) => {
                    const key = `${category.partCategoryId}-${account.accountId}`;
                    const cellData = cellDataByKey[key];
                    const disabled = !cellData?.enabled;

                    return (
                      <td
                        key={key}
                        className={`px-3 py-2 text-center text-sm ${disabled ? "bg-gray-100 text-gray-400" : "text-gray-800"}`}
                      >
                        {disabled ? (
                          ""
                        ) : (
                          <input
                            type="number"
                            step="0.01"
                            value={priceInputs[key] ?? ""}
                            onChange={(e) =>
                              setPriceInputs((prev) => ({
                                ...prev,
                                [key]: e.target.value,
                              }))
                            }
                            onBlur={() => handleCellBlur(category.partCategoryId, account.accountId)}
                            className="w-full rounded border border-gray-300 px-2 py-1 text-right text-sm focus:border-primary-500 focus:ring-primary-500"
                            placeholder="0.00"
                            disabled={updateVehiclePricingMatrix.isLoading}
                          />
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-center text-sm font-semibold text-primary-700">
                    {bestPrice != null ? PriceFormat(bestPrice) : "—"}
                  </td>
                </tr>
              );
            })}
            <tr className="bg-gray-50">
              <td className="px-3 py-2 text-sm font-semibold text-gray-900">
                Total Best
              </td>
              {accounts.map((account) => (
                <td key={`total-${account.accountId}`} className="px-3 py-2" />
              ))}
              <td className="px-3 py-2 text-center text-sm font-semibold text-primary-700">
                {PriceFormat(totalBest)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
